import { Evidence, EvidenceUploadMode } from 'api';
import { useMemo } from 'react';

import { OfflinePhoto, useGetEvidenceList, useOfflineEvidence } from '../../../evidence';
import { isOfflineEvidence, isOnlineEvidence, MaterialEvidence, OfflineEvidence, OnlineEvidence } from '../../types';
import { getAnalysisStatusAndRejectionReason } from '../../utils/getAnalysisStatusAndRejectionReason/getAnalysisStatusAndRejectionReason';
import { useEvidenceAnalysisStore } from '../useBaleAnalysis/useBaleAnalysisStore';

export const convertEvidenceToOnlineEvidence = (evidence: Evidence, mode: EvidenceUploadMode): OnlineEvidence => {
  if (!evidence.file) {
    throw new Error('Evidence file is missing');
  }

  if (!evidence.model_results) {
    // TODO: set up manual inspection evidence https://linear.app/safi/issue/AI-1781/introduce-a-common-interface-for-evidence
    throw new Error('Evidence model results are missing');
  }
  const { analysisStatus, photoRejectedReason, photoWarningReason } = getAnalysisStatusAndRejectionReason(
    mode,
    evidence.model_results
  );

  const result: OnlineEvidence = {
    analysis: evidence,
    analysisStatus,
    evidenceId: evidence.id,
    id: evidence.id.toString(),
    isLoading: false,
    loadingStatus: evidence.loaded ? 'loaded' : 'not-loaded',
    photo: evidence.file,
    photoRejectedReason,
    photoWarningReason,
    variant: 'online' as const,
  };

  return result;
};
export const convertEvidenceToOfflineEvidence = (evidence: OfflinePhoto): OfflineEvidence => {
  return {
    id: evidence.id,
    isLoading: false,
    photo: evidence.url,
    variant: 'offline' as const,
  };
};

export const filterNewOnlineEvidence = (onlineEvidence: Evidence[], existingEvidence: MaterialEvidence[]) =>
  onlineEvidence.filter(
    (evidence) =>
      !existingEvidence.filter(isOnlineEvidence).find((newEvidence) => newEvidence.evidenceId === evidence.id)
  );

export const filterNewOfflineEvidence = (offlineEvidence: OfflinePhoto[], existingEvidence: MaterialEvidence[]) =>
  offlineEvidence.filter(
    (evidence) => !existingEvidence.filter(isOfflineEvidence).find((newEvidence) => newEvidence.id === evidence.id)
  );

export const useGetEvidence = (mode: EvidenceUploadMode, parentId: string | number) => {
  const { evidence: existingEvidence } = useEvidenceAnalysisStore();

  const {
    data: onlineEvidence,
    isLoading: isOnlineEvidenceLoading,
    refetchEvidence,
  } = useGetEvidenceList({
    mode,
    parentId,
  });

  const { offlinePhotos: offlineEvidence, offlineIsLoading: isOfflineEvidenceLoading } = useOfflineEvidence({
    mode,
    parentId,
  });

  const evidence: MaterialEvidence[] = useMemo(() => {
    const formattedOnlineEvidence = filterNewOnlineEvidence(onlineEvidence ?? [], existingEvidence).map((evidence) =>
      convertEvidenceToOnlineEvidence(evidence, mode)
    );

    const formattedOfflineEvidence = filterNewOfflineEvidence(offlineEvidence, existingEvidence).map(
      convertEvidenceToOfflineEvidence
    );

    return [...existingEvidence, ...formattedOnlineEvidence, ...formattedOfflineEvidence];
  }, [existingEvidence, onlineEvidence, offlineEvidence, mode]);

  return { evidence, isLoading: isOnlineEvidenceLoading || isOfflineEvidenceLoading, refetchEvidence };
};
